import type { OrderAttachmentKind, OrderStatus } from "@/modules/orders/domain/contracts";
import { AppImage } from "@/shared/ui/AppImage";
import { PresignedImageUploadTrigger } from "@/shared/ui/PresignedImageUploadTrigger";
import { Button, Label, Modal, Textarea } from "@devhop/ui";
import {
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  PackageIcon,
  RefreshCwIcon,
  Undo2Icon,
  TruckIcon,
  XIcon,
} from "lucide-react";
import { nanoid } from "nanoid";
import { useState } from "react";

const STATUS_LABELS: Record<string, string> = {
  pending_payment: "ລໍຖ້າຊຳລະ",
  confirmed: "ຢືນຢັນ",
  packing: "ກຳລັງແພັກ",
  shipped: "ສົ່ງແລ້ວ",
  completed: "ສຳເລັດ",
  return_in_transit: "ສິນຄ້າຕີກັບກຳລັງມາ",
  returned: "ຕີກັບ / ຮັບຄືນຄັງ",
  cancelled: "ຍົກເລີກ",
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending_payment: <ClockIcon className="size-4" />,
  confirmed: <CheckIcon className="size-4" />,
  packing: <PackageIcon className="size-4" />,
  shipped: <TruckIcon className="size-4" />,
  completed: <CheckCircleIcon className="size-4" />,
  return_in_transit: <RefreshCwIcon className="size-4" />,
  returned: <Undo2Icon className="size-4" />,
  cancelled: <XIcon className="size-4" />,
};

export type StatusChangePendingAttachment = {
  id: string;
  fileKey: string;
  blurhash?: string | null;
};

type OrderStatusChangeFormProps = {
  orderId: string;
  nextStatuses: OrderStatus[];
  onSubmit: (
    status: OrderStatus,
    note: string,
    attachments: { fileKey: string; blurhash?: string | null }[],
  ) => void | Promise<void>;
};

function attachmentKindForTargetStatus(
  status: OrderStatus,
): OrderAttachmentKind | null {
  if (status === "packing") return "packing";
  if (status === "shipped") return "shipping_document";
  return null;
}

export function OrderStatusChangeForm({
  orderId,
  nextStatuses,
  onSubmit,
}: OrderStatusChangeFormProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusToConfirm, setStatusToConfirm] = useState<OrderStatus | null>(
    null,
  );
  const [note, setNote] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<
    StatusChangePendingAttachment[]
  >([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);

  const openDialog = (status: OrderStatus) => {
    setStatusToConfirm(status);
    setNote("");
    setPendingAttachments([]);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setStatusToConfirm(null);
    setNote("");
    setPendingAttachments([]);
  };

  const addPending = (fileKey: string, blurhash?: string | null) => {
    setPendingAttachments((prev) => [
      ...prev,
      { id: nanoid(), fileKey, blurhash: blurhash ?? null },
    ]);
  };

  const addPendingMany = (keys: string[], blurhashes: string[]) => {
    setPendingAttachments((prev) => {
      const next = [...prev];
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (!k) continue;
        const bh = blurhashes[i];
        next.push({
          id: nanoid(),
          fileKey: k,
          blurhash: bh || null,
        });
      }
      return next;
    });
  };

  const removePending = (id: string) => {
    setPendingAttachments((prev) => prev.filter((p) => p.id !== id));
  };

  const handleConfirm = async () => {
    if (!statusToConfirm || submitBusy || uploadBusy) return;
    setSubmitBusy(true);
    try {
      const payload = pendingAttachments.map(({ fileKey, blurhash }) => ({
        fileKey,
        blurhash,
      }));
      await Promise.resolve(onSubmit(statusToConfirm, note, payload));
      closeDialog();
    } finally {
      setSubmitBusy(false);
    }
  };

  if (nextStatuses.length === 0) return null;

  const statusLabel = statusToConfirm
    ? (STATUS_LABELS[statusToConfirm] ?? statusToConfirm)
    : "";

  const attachmentKind = statusToConfirm
    ? attachmentKindForTargetStatus(statusToConfirm)
    : null;
  const showAttachmentUpload = attachmentKind != null;

  const keyPrefix =
    attachmentKind === "packing"
      ? `uploads/orders/${orderId}/packing`
      : attachmentKind === "shipping_document"
        ? `uploads/orders/${orderId}/shipping`
        : "uploads/orders";

  const confirmDisabled = submitBusy || uploadBusy;

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {nextStatuses.map((s) => (
          <Button
            key={s}
            type="button"
            variant={
              s === "cancelled"
                ? "destructive"
                : s === "returned" || s === "return_in_transit"
                  ? "secondary"
                  : "default"
            }
            size="sm"
            className="h-9"
            onClick={() => openDialog(s)}
          >
            {STATUS_ICONS[s]}
            {STATUS_LABELS[s] ?? s}
          </Button>
        ))}
      </div>

      <Modal
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setStatusToConfirm(null);
            setNote("");
            setPendingAttachments([]);
          }
        }}
      >
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">ປ່ຽນສະຖານະເປັນ {statusLabel}</h3>
          <div className="space-y-2">
            <Label
              htmlFor="order-status-note-dialog"
              className="text-muted-foreground text-sm"
            >
              ຫມາຍເຫດ (ບໍ່ບັງຄັບ)
            </Label>
            <Textarea
              id="order-status-note-dialog"
              className="w-full"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ອະທິບາຍເຫດຜົນ ຫຼື ລາຍລະອຽດເພີ່ມເຕີມ"
              rows={3}
            />
          </div>

          {showAttachmentUpload && (
            <div className="space-y-2 rounded-md border border-dashed bg-muted/20 p-3">
              <Label className="text-muted-foreground text-sm">
                {attachmentKind === "packing"
                  ? "ຮູບການແພັກ (ບໍ່ບັງຄັບ)"
                  : "ຮູບບິນຂົນສົ່ງ (ບໍ່ບັງຄັບ)"}
              </Label>
              <p className="text-muted-foreground text-xs">
                ອັບໂຫຼດກ່ອນກົດຢືນຢັນ — ລະບົບຈະບັນທຶກຮູບຫຼັງປ່ຽນສະຖານະສຳເລັດ
              </p>
              <PresignedImageUploadTrigger
                keyPrefix={keyPrefix}
                label={
                  attachmentKind === "packing"
                    ? "ເພີ່ມຮູບແພັກ"
                    : "ເພີ່ມຮູບບິນຂົນສົ່ງ"
                }
                multiple
                disabled={submitBusy}
                isSaving={false}
                onBusyChange={setUploadBusy}
                onSuccess={(key, blurhash) => addPending(key, blurhash)}
                onSuccessMultiple={(keys, blurhashes) =>
                  addPendingMany(keys, blurhashes)
                }
              />
              {pendingAttachments.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {pendingAttachments.map((p) => (
                    <div
                      key={p.id}
                      className="group relative aspect-square overflow-hidden rounded-md border bg-background"
                    >
                      <AppImage
                        src={p.fileKey}
                        alt=""
                        className="size-full object-cover"
                        showLoading={false}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 size-7 opacity-0 transition-opacity group-hover:opacity-100"
                        disabled={submitBusy || uploadBusy}
                        onClick={() => removePending(p.id)}
                        title="ລຶບອອກຈາກລາຍການລໍຖ້າ"
                      >
                        <XIcon className="size-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={closeDialog}
              disabled={submitBusy}
            >
              ຍົກເລີກ
            </Button>
            <Button onClick={() => void handleConfirm()} disabled={confirmDisabled}>
              {confirmDisabled ? "ກຳລັງດຳເນີນການ..." : "ຢືນຢັນ"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
